import { ChangeEvent, useCallback, useState } from 'react'
import classNames from 'classnames/bind'
import branch from 'recompose/branch'
import renderNothing from 'recompose/renderNothing'

import IconLightCross from 'app/common/icons/IconLightCross'
import ImageClickable from 'app/common/ui/ImageClickable'
import IconSettings from 'app/common/icons/IconSettings'
import PlainCheckbox from 'app/common/ui-next/plain-checkbox'
import Tooltip from 'app/common/ui/Tooltip'
import EditableProp from 'app/common/ui/EditableProp'
import ImagePrepare from 'app/common/components/ImagePrepare'
import type { DishesTableSettings } from 'app/components/Settings/SettingsGear/DishesGear'
import { CellLabel, Cell, Row } from 'app/common/components/Table'
import { useSaleTypesAsArray } from 'app/dataProviders/SaleTypesData'
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link'
import { CellContentSaleType } from 'app/components/common/cellContents/CellContentSaleType'
import ClickOutside from 'app/common/ui/ClickOutside'
import PlainMenu from 'app/common/ui/PlainMenu'
import PlainMenuItem from 'app/common/ui/PlainMenu/PlainMenuItem'

import enchanceDish, { Props as EnchanceDishProps } from './enchanceDish'
import styles from './Dish.scss'

// ----------------
// Универсальный компонент блюда для таблицы блюд
// Через контейнер задаются actions для работы с блюдом,
// определенные для конкретного случая использования
// ----------------
// TODO deprecated, do not use!

const cx = classNames.bind(styles)

const Photo = branch(({ src }) => !src, renderNothing)('img')

export type DishItemProps = {
  id: number
  title: string
  photo: string
  restrictions?: string
  portion: number | string
  cost: number | string
  saleType: {
    id: string
    title: string
  }
  category?: string
} & EnchanceDishProps

type Props = {
  id: number
  groupName: string
  settings: DishesTableSettings
  params: DishItemProps
  loading: boolean
  toggleItem: (e: ChangeEvent<HTMLElement>) => void
  checked: boolean // выделен (чекбокс и желтый фон
  handleDelete: (e: ChangeEvent<HTMLElement>) => void
  handleEdit?: (e: ChangeEvent<HTMLElement>) => void
  handleAdditionalSettings?: () => void
  handleSaveProp: (some: Object) => void
  handleSaleType: (value: string) => Promise<boolean>
  handleSaveImage: (e: ChangeEvent<HTMLElement>) => void
  handleDeleteImage: (e: ChangeEvent<HTMLElement>) => void
  loadingSaleType: boolean
  locked?: boolean // поля не редактируются
  lockPhoto?: boolean
  selectable?: boolean // чекбокс для выделения отсутствует
  success?: boolean // выделен зеленым (только фон), сhecked не меняет цвет фона
  className?: string
  classes?: Record<string, string>
  disableDishGear?: boolean
}

const Dish = ({
  id,
  groupName = '',
  settings,
  params: { title, photo, restrictions, portion, cost, saleType, category },
  loading,
  toggleItem,
  checked,
  handleDelete,
  handleEdit,
  handleAdditionalSettings,
  handleSaveProp,
  handleSaleType,
  handleSaveImage,
  handleDeleteImage,
  loadingSaleType,
  locked,
  lockPhoto = false,
  selectable = true,
  success,
  className,
  classes = {},
  disableDishGear,
}: Props) => {
  const htmlFor = `${groupName}_${id}`

  const { saleTypes, loading: loadingSaleTypes } = useSaleTypesAsArray()
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false)

  const toggleSettingsMenu = useCallback((e: ChangeEvent<HTMLElement>) => {
    e.preventDefault()
    setIsSettingsMenuOpen((value) => !value)
  }, [])

  const closeSettingsMenu = useCallback(() => {
    setIsSettingsMenuOpen(false)
  }, [])

  const handleSettingsOpen = useCallback(
    (e: ChangeEvent<HTMLElement>) => {
      if (handleEdit) {
        handleEdit(e)
      } else if (e && e.preventDefault) {
        e.preventDefault()
      }
      setIsSettingsMenuOpen(false)
    },
    [handleEdit],
  )

  const handleAdditionalSettingsOpen = useCallback((e: ChangeEvent<HTMLElement>) => {
    e.preventDefault()
    if (handleAdditionalSettings) {
      handleAdditionalSettings()
    }
    setIsSettingsMenuOpen(false)
  }, [handleAdditionalSettings])

  return (
    <Row loading={loading} className={cx(styles.content, { active: checked, success }, className)}>
      {selectable && (
        <CellLabel
          htmlFor={htmlFor}
          className={cx(styles.cell, classes.checkbox)}
          width={50}
          grow={0}
        >
          <PlainCheckbox
            id={htmlFor}
            value={String(id)}
            onChange={toggleItem}
            checked={checked}
            skipLabel
          />
        </CellLabel>
      )}

      <CellLabel htmlFor={htmlFor} grow={5} className={styles.cell}>
        {title}
      </CellLabel>

      {settings.photo &&
        (lockPhoto ? (
          <CellLabel htmlFor={htmlFor} alignMiddle grow={3} className={styles.cell}>
            <Photo src={photo} className={styles.photo} />
          </CellLabel>
        ) : (
          <Cell alignMiddle grow={3} className={styles.cell}>
            <ImagePrepare
              onSubmit={handleSaveImage}
              rectMinSizeWidth={100}
              rectMinSizeHeight={133}
              rectProportion={0.75}
            >
              {(handleFileOpen) =>
                !photo ? (
                  <a href="open" onClick={handleFileOpen} className={styles.link}>
                    {'+ Добавить фото'}
                  </a>
                ) : (
                  <ImageClickable
                    src={photo}
                    className={styles.photo}
                    onReplace={handleFileOpen}
                    onDelete={handleDeleteImage}
                  />
                )
              }
            </ImagePrepare>
          </Cell>
        ))}

      {settings.restrictions && (
        <CellLabel htmlFor={htmlFor} className={styles.cell} alignMiddle grow={3}>
          {restrictions}
        </CellLabel>
      )}

      {settings.portion &&
        (locked ? (
          <CellLabel
            htmlFor={htmlFor}
            alignMiddle
            grow={2}
            className={cx(styles.cell, classes.portion)}
          >
            {portion}
          </CellLabel>
        ) : (
          <Cell alignMiddle grow={2} className={cx(styles.cell, classes.portion)}>
            <EditableProp
              right
              value={String(portion)}
              handleSave={handleSaveProp}
              name="portion"
            />
          </Cell>
        ))}

      {settings.cost &&
        (locked ? (
          <CellLabel
            htmlFor={htmlFor}
            alignMiddle
            grow={3}
            className={cx(styles.cell, classes.cost)}
          >
            {cost}
          </CellLabel>
        ) : (
          <Cell alignMiddle grow={3} className={cx(styles.cell, classes.cost)}>
            <EditableProp
              className={styles.wp}
              right
              value={String(cost)}
              handleSave={handleSaveProp}
              name="cost"
            />
          </Cell>
        ))}

      {settings.saleType && locked && (
        <CellLabel htmlFor={htmlFor} grow={4} className={styles.cell}>
          {saleTypes.find(({ id: stId }) => stId === saleType.id)?.title}
        </CellLabel>
      )}

      {settings.saleType && !locked && (
        <Cell grow={4} className={styles.cell}>
          <CellContentSaleType
            id={String(id)}
            currentSaleTypeId={saleType.id}
            pending={loadingSaleTypes || loadingSaleType}
            saleTypes={saleTypes}
            locked={loading || locked}
            onChangeSaleType={handleSaleType}
          />
        </Cell>
      )}

      {settings.category && (
        <CellLabel htmlFor={htmlFor} grow={2} className={styles.cell}>
          {category}
        </CellLabel>
      )}

      {settings.setup &&
        (locked ? (
          <CellLabel htmlFor={htmlFor} grow={1} className={cx(styles.cell, classes.setup)}>
            &nbsp;
          </CellLabel>
        ) : (
          <Cell grow={1} className={cx(styles.cell, classes.setup)}>
            <div className={styles.links}>
              {handleEdit && !disableDishGear && (
                <ClickOutside className={styles.menuRoot} onClickOutside={closeSettingsMenu}>
                  <PlainButtonAsLink onClick={toggleSettingsMenu as () => void}>
                    <Tooltip text="Настроить блюдо">
                      <IconSettings className={styles.iconSettings} />
                    </Tooltip>
                  </PlainButtonAsLink>
                  {isSettingsMenuOpen && (
                    <PlainMenu className={styles.menu}>
                      <PlainMenuItem onPropagationCatch={closeSettingsMenu}>
                        <button type="button" onClick={handleSettingsOpen as () => void}>
                          {'Настройки'}
                        </button>
                      </PlainMenuItem>
                      {handleAdditionalSettings && (
                        <PlainMenuItem onPropagationCatch={closeSettingsMenu}>
                          <button type="button" onClick={handleAdditionalSettingsOpen as () => void}>
                            {'Дополнительные настройки'}
                          </button>
                        </PlainMenuItem>
                      )}
                    </PlainMenu>
                  )}
                </ClickOutside>
              )}
              {handleDelete && (
                <PlainButtonAsLink onClick={handleDelete as () => void}>
                  <Tooltip text="Удалить блюдо">
                    <IconLightCross className={styles.iconCross} />
                  </Tooltip>
                </PlainButtonAsLink>
              )}
            </div>
          </Cell>
        ))}

    </Row>
  )
}

export default enchanceDish(Dish)
